export const CORE_CHARACTERS = [
  "心",
  "守",
  "信",
  "义",
  "勇",
  "恩",
  "志",
  "真",
  "和",
  "别",
  "念",
  "归",
  "行",
  "静",
  "诚",
  "诺",
  "舍",
  "起",
  "安",
  "愿",
  "清",
  "明",
  "久",
  "新",
] as const;

export type CoreCharacter = (typeof CORE_CHARACTERS)[number];
export type ExpressionTopic = "守住" | "感谢" | "告别" | "出发" | "不便直说";
export type RefinementSource = "ai" | "fallback" | "safety";

export interface RefinementResult {
  core_value: string;
  core_character: CoreCharacter;
  phrases: [string, string, string];
  reflection: string;
  recommended_bamboo: "wind" | "rain" | "sparse" | "dense";
  source: RefinementSource;
}

const FALLBACKS: Record<ExpressionTopic, Omit<RefinementResult, "source">> = {
  守住: {
    core_value: "守诺",
    core_character: "守",
    phrases: ["守心不改", "持诺而行", "静守此心"],
    reflection: "你想守住的，不必喧哗，也可以在行动里慢慢变得清楚。",
    recommended_bamboo: "dense",
  },
  感谢: {
    core_value: "知恩",
    core_character: "恩",
    phrases: ["念恩于心", "来日相报", "一叶知恩"],
    reflection: "你没有把得到的善意当作理所当然。记得，也是一种回应。",
    recommended_bamboo: "rain",
  },
  告别: {
    core_value: "善别",
    core_character: "别",
    phrases: ["善始善别", "别后无憾", "此去珍重"],
    reflection: "你想离开，却仍愿意把这一程好好放下。",
    recommended_bamboo: "sparse",
  },
  出发: {
    core_value: "笃行",
    core_character: "行",
    phrases: ["心有所向", "新程自此", "行而不辍"],
    reflection: "你并非抹去来路，只是决定让脚步回应心里的方向。",
    recommended_bamboo: "wind",
  },
  不便直说: {
    core_value: "含真",
    core_character: "真",
    phrases: ["不言亦明", "心藏一诺", "留白有声"],
    reflection: "有些话不必交出全部细节，也仍可以留下真实的分量。",
    recommended_bamboo: "sparse",
  },
};

const CHARACTER_HINTS: Array<[RegExp, CoreCharacter]> = [
  [/承诺|约定|答应|兑现|团队/, "诺"],
  [/离开|告别|分别|放下/, "别"],
  [/开始|出发|项目|行动|前路/, "行"],
  [/感谢|感激|帮助|恩情/, "恩"],
  [/回家|回去|归来/, "归"],
  [/平安|安全|安稳/, "安"],
  [/勇敢|害怕|困难/, "勇"],
  [/真实|真话|坦诚/, "真"],
  [/清醒|清白|清楚/, "清"],
  [/长久|坚持|一直/, "久"],
];

export function isExpressionTopic(value: unknown): value is ExpressionTopic {
  return typeof value === "string" && value in FALLBACKS;
}

export function containsSensitiveText(value: string): boolean {
  return (
    /(?:^|\D)1[3-9]\d{9}(?:\D|$)/.test(value) ||
    /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(value) ||
    /(?:身份证|住址|家庭地址|账号|银行卡|微信号|QQ号|手机号|电话)[：:\s]*[\w\d-]{4,}/i.test(value)
  );
}

export function isHighRiskText(value: string): boolean {
  return /自杀|想死|不想活|结束生命|伤害自己|割腕|杀了(?:他|她|他们|她们|人)|报复杀人|炸掉/.test(value);
}

export function safetyResult(): RefinementResult {
  return {
    core_value: "先求助",
    core_character: "安",
    phrases: ["先保平安", "请寻援手", "此刻别独行"],
    reflection: "你写下的内容可能涉及紧急安全风险。请立即向身边可信任的人或当地紧急援助求助；这里不把危险处境诗意化。",
    recommended_bamboo: "rain",
    source: "safety",
  };
}

export function createFallback(topic: ExpressionTopic, input = ""): RefinementResult {
  const base = FALLBACKS[topic];
  const hinted = CHARACTER_HINTS.find(([pattern]) => pattern.test(input))?.[1];
  return {
    ...base,
    core_character: hinted ?? base.core_character,
    phrases: [...base.phrases],
    source: "fallback",
  };
}

export function countHanCharacters(value: string): number {
  return (value.match(/[\u3400-\u9fff]/g) ?? []).length;
}

function isCoreCharacter(value: unknown): value is CoreCharacter {
  return typeof value === "string" && (CORE_CHARACTERS as readonly string[]).includes(value);
}

function isBambooStyle(value: unknown): value is RefinementResult["recommended_bamboo"] {
  return value === "wind" || value === "rain" || value === "sparse" || value === "dense";
}

export function normalizeModelResult(value: unknown, topic: ExpressionTopic, input: string): RefinementResult {
  const fallback = createFallback(topic, input);
  if (!value || typeof value !== "object") return fallback;
  const candidate = value as Record<string, unknown>;
  const phrases = Array.isArray(candidate.phrases)
    ? candidate.phrases.filter(
        (phrase): phrase is string =>
          typeof phrase === "string" &&
          phrase.trim().length > 0 &&
          phrase.trim().length <= 16 &&
          countHanCharacters(phrase) <= 8 &&
          !containsSensitiveText(phrase),
      )
    : [];

  if (!isCoreCharacter(candidate.core_character) || phrases.length !== 3) return fallback;

  const reflection = typeof candidate.reflection === "string" ? candidate.reflection.trim() : "";
  const coreValue = typeof candidate.core_value === "string" ? candidate.core_value.trim() : "";
  if (!reflection || reflection.length > 72 || !coreValue || coreValue.length > 8) return fallback;
  if (containsSensitiveText(reflection) || isHighRiskText(reflection)) return fallback;

  return {
    core_value: coreValue,
    core_character: candidate.core_character,
    phrases: [phrases[0].trim(), phrases[1].trim(), phrases[2].trim()],
    reflection,
    recommended_bamboo: isBambooStyle(candidate.recommended_bamboo)
      ? candidate.recommended_bamboo
      : fallback.recommended_bamboo,
    source: "ai",
  };
}
